import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  TextInput,
  FlexBox,
  FormField,
  FormLabel,
  FormInputGroup,
  StandardForm,
  VisuallyHidden,
  FormError,
  useScreenClass,
} from '@vp/swan';
import { useTranslations } from '@vp/om-translations';
import { useResizeDetector } from 'react-resize-detector';
import { persistTaxId } from '../api/taxIdApi';
import {
  getTaxIdValidity,
  getNormalizedTaxId,
} from '../validation/taxIdValidator';
import { isTrustedOrigin } from '../validation/origin';
import {
  EVENT_TAX_ID_INIT,
  EVENT_TAX_ID_INIT_CONTEXT,
  EVENT_TAX_ID_SET_HEIGHT,
  EVENT_TAX_ID_SAVE_SUCCESS,
  EVENT_TAX_ID_SAVE_FAILURE,
} from '../constants/events';

export const TaxIdEntryMain = () => {
  const [taxIdMessages] = useTranslations(['taxIdMessages']);

  const [authToken, setAuthToken] = useState();
  const [environment, setEnvironment] = useState();
  const [placeholderTextKey, setPlaceholderTextKey] =
    useState('enterYourTaxId');
  const [contextInitialized, setContextInitialized] = useState(false);

  // init message
  useEffect(() => {
    window.parent.postMessage({ type: EVENT_TAX_ID_INIT }, '*');
  }, []);

  useEffect(() => {
    const receiveMessage = (event) => {
      if (isTrustedOrigin(event?.origin)) {
        switch (event.data?.type) {
          case EVENT_TAX_ID_INIT_CONTEXT:
            setAuthToken(event.data.idToken);
            setEnvironment(event.data.environment);
            setContextInitialized(true);
            setPlaceholderTextKey(
              event.data.isTaxIdSet ? 'reEnterYourTaxId' : 'enterYourTaxId'
            );
            break;
          default:
            break;
        }
      }
    };

    window.addEventListener('message', receiveMessage);

    return function cleanup() {
      window.removeEventListener('message', receiveMessage);
    };
  }, []);

  // resize handling
  const onResize = useCallback((payload) => {
    let requestedHeight = 0;
    if (payload.height !== 0) {
      // add a bit of padding
      requestedHeight = payload.height + 15;
    }
    window.parent.postMessage(
      { type: EVENT_TAX_ID_SET_HEIGHT, height: requestedHeight },
      '*'
    );
  }, []);

  const { ref } = useResizeDetector({ onResize, handleWidth: false });

  // tax Id entry state management
  const [taxIdInputValue, setTaxIdInputValue] = useState('');
  const [isTaxIdValid, setIsTaxIdValid] = useState(true);
  const [taxIdSaveInProgress, setTaxIdSaveInProgress] = useState(false);
  const [validityMessageKey, setValidityMessageKey] = useState('');

  const taxIdInputRef = React.createRef();

  // handler for changes to the text input. Clears out the taxIdValidity since we are only running validations on submission.
  const onFieldChange = () => {
    const value = taxIdInputRef.current ? taxIdInputRef.current.value : '';
    setTaxIdInputValue(value);
    setIsTaxIdValid(true);
    setValidityMessageKey('');
  };

  // function that orchestrates checking validity of the entered tax id, persisting it, and messaging results to the parent.
  const saveTaxId = () => {
    const value = getNormalizedTaxId(taxIdInputRef.current?.value);
    const taxIdValidity = getTaxIdValidity(value);
    if (!taxIdValidity.isValid) {
      setIsTaxIdValid(false);
      setValidityMessageKey(taxIdValidity.errorMessageKey);
      return;
    }

    setTaxIdSaveInProgress(true);
    persistTaxId({ taxId: value, authToken, environment })
      .then((taxIdReference) => {
        window.parent.postMessage(
          { type: EVENT_TAX_ID_SAVE_SUCCESS, taxIdRef: taxIdReference },
          '*'
        );
        setTaxIdSaveInProgress(false);
      })
      .catch((error) => {
        window.parent.postMessage(
          { type: EVENT_TAX_ID_SAVE_FAILURE, error },
          '*'
        );
        setTaxIdSaveInProgress(false);
      });
  };

  // handler needed for form submission via enter key
  const onSubmit = (event) => {
    event.preventDefault();
    saveTaxId();
  };

  const screenClass = useScreenClass();
  const isMobileView = screenClass === 'xs';

  return (
    <Box backgroundColor="strong" ref={ref}>
      {contextInitialized && (
        <StandardForm onSubmit={onSubmit}>
          <FormField>
            <VisuallyHidden>
              <FormLabel>{taxIdMessages[placeholderTextKey]}</FormLabel>
            </VisuallyHidden>
            <FormInputGroup m="3">
              <FlexBox flexDirection={isMobileView ? 'column' : 'row'} gap="3">
                <Box style={{ width: '100%' }}>
                  <TextInput
                    disabled={taxIdSaveInProgress}
                    mr="5"
                    onChange={onFieldChange}
                    value={taxIdInputValue}
                    skin={isTaxIdValid ? 'standard' : 'error'}
                    ref={taxIdInputRef}
                    placeholder={taxIdMessages[placeholderTextKey]}
                  />
                  {validityMessageKey && (
                    <FormError>{taxIdMessages[validityMessageKey]}</FormError>
                  )}
                </Box>
                <Box>
                  <Button
                    skin="secondary"
                    onClick={() => {
                      saveTaxId();
                    }}
                    disabled={taxIdSaveInProgress}
                    width={isMobileView ? 'full-width' : 'standard'}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {taxIdMessages.apply}
                  </Button>
                </Box>
              </FlexBox>
            </FormInputGroup>
          </FormField>
        </StandardForm>
      )}
    </Box>
  );
};
